/**
 * CombatManager — battle orchestration: turn flow, damage, victory/defeat.
 *
 * Supports multiple enemies. The "current target" is the first living enemy.
 * Holds the ModifierBag for the current battle — all stat queries go through it.
 */

import { GameCharacter } from "../../character/GameCharacter";
import { PlayerCharacter } from "../../character/PlayerCharacter";
import { EnemyCharacter } from "../../character/EnemyCharacter";
import { CombatState } from "../../character/CombatState";
import { getEncounter } from "../../character/EnemyDatabase";
import { HIGH_ALERT } from "../../character/effects";
import { ModifierBag } from "../../../framework/stats";
import { buildBattleBag } from "../../character/stats";
import * as S from "../../character/stats/StatKeys";
import * as H from "../../character/stats/HookKeys";


export interface CombatResult {
  outcome: "victory" | "defeat";
  gold?: number;
}

export class CombatManager {
  readonly player: PlayerCharacter;
  readonly enemies: EnemyCharacter[];
  readonly state: CombatState;
  /** The modifier bag for this battle. All stat queries go through here. */
  readonly bag: ModifierBag;
  /** Whether this node was on high alert. */
  readonly isAlert: boolean;

  private turnNumber = 0;

  constructor(player: PlayerCharacter, isBoss: boolean, isElite: boolean, alert: boolean = false, depth: number = 0) {
    this.player = player;
    this.isAlert = alert;
    this.enemies = getEncounter(isBoss, isElite, depth);

    // Apply alert buffs: boost stats and add the status effect for display
    if (alert) {
      for (const enemy of this.enemies) {
        enemy.effects.apply(HIGH_ALERT);
        enemy.armor += enemy.effects.get("high_alert")!.getModifier("armor");
        enemy.attack += enemy.effects.get("high_alert")!.getModifier("attack");
      }
    }

    // Build the modifier bag from player's current state
    this.bag = buildBattleBag(player);

    // Resolve maxAp from the bag (base + any bonuses)
    const maxAp = this.bag.get(S.AP_MAX_FLAT);
    this.state = new CombatState(maxAp);

    // Fire battle start hooks
    this.bag.runHooks(H.ON_BATTLE_START);
  }

  /** The first living enemy (current attack target). */
  get currentTarget(): EnemyCharacter | null {
    return this.enemies.find(e => e.alive) ?? null;
  }

  /** All enemies alive. */
  get livingEnemies(): EnemyCharacter[] {
    return this.enemies.filter(e => e.alive);
  }

  /** Whether all enemies are dead. */
  get allDead(): boolean {
    return this.enemies.every(e => e.dead);
  }

  /** Deal damage to any character (through their armor). */
  dealDamage(target: GameCharacter, amount: number): { dead: boolean; hpDamage: number; absorbed: number } {
    const armorBefore = target.armor;
    const hpDamage = target.damage(amount);
    const absorbed = armorBefore - target.armor;
    return { dead: target.dead, hpDamage, absorbed };
  }

  /** A specific enemy attacks the player (through player's combat armor). Returns true if player is dead. */
  enemyAttackPlayer(enemy: GameCharacter): boolean {
    const hpDamage = this.state.applyDamage(enemy.attack);
    this.player.damage(hpDamage);
    this.bag.runHooks(H.ON_DAMAGE_TAKEN, { rawDamage: enemy.attack, actualDamage: hpDamage });
    return !this.player.alive;
  }

  /** Spend 1 AP. Returns true if spent successfully. */
  spendAp(): boolean {
    return this.state.spendAp();
  }

  /** Start a new player turn (reset AP, clear armor, apply per-turn modifiers). */
  startPlayerTurn(): void {
    this.turnNumber++;
    const armor = this.bag.get(S.ARMOR_FLAT);
    this.state.startTurn();
    this.state.armor = armor;
    this.bag.runHooks(H.ON_TURN_START, { turnNumber: this.turnNumber });
  }

  /** Get the total gold reward for winning (modified by bag). */
  getReward(): number {
    const baseGold = this.enemies.reduce((sum, e) => sum + e.goldReward, 0);
    return this.bag.get(S.GOLD_PERCENT, baseGold);
  }
}
